import { Fragment } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import Accordion from "@/components/base/Accordion";
import SectionHeading from "@/components/base/SectionHeading";
import Eyebrow from "@/components/base/Eyebrow";
import MicroLabel from "@/components/base/MicroLabel";
import Callout from "@/components/base/Callout";
import InlineNote from "@/components/base/InlineNote";
import Tag from "@/components/base/Tag";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import BrokerStrip from "@/components/feature/BrokerStrip";
import LatencyTimeline from "@/pages/order-execution/components/LatencyTimeline";
import CostCalculator from "@/pages/order-execution/components/CostCalculator";
import SessionLiquidity from "@/pages/order-execution/components/SessionLiquidity";
import RiskTools from "@/pages/order-execution/components/RiskTools";
import useInView from "@/hooks/useInView";

const journey = [
  {
    title: "You submit the order",
    text: "You pick the type, size and any trigger or limit levels, then send it on its way.",
    watch: "The clock starts here — the price on your screen is already a fraction of a second old.",
  },
  {
    title: "Broker routes it",
    text: "Your order is matched in-house or passed to liquidity providers, depending on your broker's model.",
    watch: "Routing costs time. On a slow link, price can move before your order even arrives.",
  },
  {
    title: "The market fills it",
    text: "A limit order fills at your price or better; a market order takes the next available price.",
    watch: "That next available price is rarely the one you clicked — that gap is slippage.",
  },
];

const speedTradeoff = [
  {
    anchor: "Market order",
    title: "You buy speed",
    tone: "primary",
    to: "/market-orders",
    linkLabel: "How market orders fill",
    points: [
      "Fills almost instantly at the next available price.",
      "Price is not guaranteed once the book starts moving.",
      "The right call when getting in or out now matters more than the exact level.",
    ],
  },
  {
    anchor: "Limit order",
    title: "You buy price",
    tone: "accent",
    to: "/limit-orders",
    linkLabel: "How limit orders fill",
    points: [
      "Fills only at your price or better — zero slippage by design.",
      "No guarantee it fills; price may never reach your level.",
      "The right call when the level matters more than the timing.",
    ],
  },
];

const slippage = [
  { label: "Price you clicked", value: "1.08450", note: "Before the spike" },
  {
    label: "Price when filled",
    value: "1.08496",
    note: "≈ 75 ms after the click — machine time only, your reaction comes before it",
  },
  { label: "Slippage", value: "4.6 pips", note: "≈ $46 on 1 lot", accent: true },
];

const costRows = [
  {
    cost: "Spread",
    what: "The gap between the bid and the ask.",
    when: "Every market order, instantly.",
    known: "Yes — visible before you trade.",
  },
  {
    cost: "Commission",
    what: "A fixed broker fee charged per lot traded.",
    when: "Billed once as a round-trip fee — entry and exit together.",
    known: "Yes — published up front.",
  },
  {
    cost: "Swap / rollover",
    what: "Overnight interest for holding a position past 17:00 New York.",
    when: "Charged for every night the trade stays open.",
    known: "Mostly — the rate is known, the total depends on nights held.",
  },
  {
    cost: "Slippage",
    what: "The difference between the price you expected and the price you got.",
    when: "Volatile or thin markets.",
    known: "No — this is the wildcard.",
  },
];

const costExample = [
  { label: "Spread", value: "$10.00", note: "1.0 pip" },
  { label: "Commission", value: "$7.00", note: "Round-turn" },
  { label: "Slippage", value: "$5.00", note: "0.5 pip average · 4.6 pips in the news example" },
  { label: "Swap (1 night)", value: "$3.20", note: "Overnight debit" },
  { label: "Total cost", value: "$25.20", note: "0.25% of a $10k account", total: true },
];

const myths = [
  {
    myth: "My broker hunts my stop-loss",
    why: "The stop fills, then price turns straight back. You picked the level yourself, so a loss that reverses seconds later feels like someone reached in and took it.",
    reality:
      "Stops sit at visible round numbers, so they cluster at the same few levels. When price reaches one, every triggered order hits the market at once and pushes it through — then the burst is spent, the selling it created is gone, and price drifts back to where the real flow sits. That round trip is what looks like a hunt, and no broker had to be involved.",
  },
  {
    myth: "Slippage only ever happens to me",
    why: "You notice it most when it costs you money, so it dominates your memory of an otherwise fine session.",
    reality:
      "Slippage is a market-wide event. When liquidity thins or news hits, every participant on that instrument is repriced at once — and it sometimes lands in your favour, which the same memory quietly drops. It is the market moving, not your account being singled out.",
  },
  {
    myth: "A limit order guarantees I get filled",
    why: "A limit order sitting on the chart looks like a done deal, so traders build a plan around a fill that never arrives.",
    reality:
      "A limit guarantees price, not a fill. If price never reaches your level, nothing happens at all; if it only touches it, a thin book can leave you part-filled — half a position while the move runs without the rest of it.",
  },
];

const bestPractices = [
  {
    habit: "Set your stop and target when you enter, not after the trade turns.",
    why: "An exit decided mid-move usually goes out as a market order, straight across a spread that has already widened.",
    cost: "Spread · Slippage",
  },
  {
    habit: "Keep stops off the round numbers everyone else is watching.",
    why: "Triggered stops cluster at those levels. When price reaches them the orders pile through and the fill lands past your stop.",
    cost: "Slippage",
  },
  {
    habit: "Stay out of market orders for the first seconds after a release.",
    why: "Both costs spike at the same moment — the 4.6-pip fill above happened in the 75 ms it took the order to travel, after you had already clicked.",
    cost: "Spread · Slippage",
  },
  {
    habit: "Match the order type to the session, not just to the setup.",
    why: "On majors the London–New York overlap is where spreads are tightest. In a quiet Sydney hour the same fill can cost five times as much in spread — $20 instead of $4 — while commission and swap do not move at all.",
    cost: "Spread",
  },
  {
    habit: "Price all four costs into the plan, not just the spread.",
    why: "Spread and commission are quoted before you click, swap accrues every night you hold, and slippage is the one you can only manage.",
    cost: "All four costs",
  },
];

const faqs = [
  {
    q: "What is the difference between spread and slippage?",
    a: "The spread is the gap between the bid and the ask, and it is known before you trade. Slippage is the difference between the price you expected and the price you were actually filled at — it appears only when the market moves faster than your order arrives. The spread is a cost you can see in advance; slippage is a cost you feel afterwards.",
  },
  {
    q: "Why did my order fill at a different price than I clicked?",
    a: "Between the moment you clicked and the moment your order reached the market, the quote moved. Market orders take the next available price, not the last one you saw, so a fast-moving or thin market can hand you a worse fill. That gap is slippage.",
  },
  {
    q: "Can slippage ever work in my favour?",
    a: "Yes. Positive slippage happens when the next available price is better than the one you expected — most common with market orders in fast, deep markets. It is less frequent than negative slippage, but it does occur, and a limit order guarantees it by never filling worse than your level.",
  },
  {
    q: "Do limit orders slip?",
    a: "No. A limit order can only fill at your price or better, so by definition it never slips against you. The trade-off is fill certainty: it may not fill at all if price never reaches your level.",
  },
  {
    q: "Why is slippage worst during news releases?",
    a: "Major releases like Non-Farm Payrolls or central-bank decisions pull quotes around in milliseconds and briefly thin the order book. Orders queue up while prices jump, so the next available price can be several pips away from the one on your screen. Spreads widen at the same time, doubling the damage.",
  },
  {
    q: "Which session has the lowest execution costs?",
    a: "The London–New York overlap, 13:00–17:00 GMT. It is the deepest, most liquid window of the day, so spreads are tightest and slippage is smallest. The quiet Sydney session, by contrast, has thin liquidity and noticeably wider spreads.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

export default function OrderExecution() {
  const { ref: pipelineRef, inView: flowActive } = useInView<HTMLDivElement>(0.25);

  return (
    <>
      <PageHero
        eyebrow="Execution"
        title="Order Execution & Slippage"
        description="Two traders with the same idea can get very different fills. Here is what happens between clicking an order and seeing it appear in your account — and how to keep execution costs low."
      />

      {/* Journey */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="The journey of an order"
            title="From click to fill in three steps"
            description="Understanding each stage — and where it can go wrong — explains almost every confusing fill you will ever see."
          />
          <div ref={pipelineRef} className="mt-12 flex flex-col md:flex-row md:items-stretch gap-4">
            {journey.map((step, index) => (
              <Fragment key={step.title}>
                <Reveal delay={index * 90} className="h-full md:flex-1">
                  <div className="h-full flex flex-col border-2 border-foreground-950 bg-background-50 p-6">
                    <div className="flex items-start justify-between gap-3 border-b-2 border-foreground-950 pb-4">
                      <span
                        className="font-heading font-black text-5xl leading-[0.8] text-foreground-950"
                        aria-hidden="true"
                      >
                        0{index + 1}
                      </span>
                      <span className="font-label text-[10px] uppercase tracking-[0.18em] text-foreground-500">
                        Step {index + 1} of {journey.length}
                      </span>
                    </div>
                    <h3 className="mt-5 font-heading font-bold uppercase tracking-tight text-lg text-foreground-950">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.text}</p>
                    <div className="mt-auto pt-5">
                      <InlineNote mark="warn" label="Watch out" className="pt-4">
                        {step.watch}
                      </InlineNote>
                    </div>
                  </div>
                </Reveal>
                {index < journey.length - 1 && (
                  <div className="hidden md:flex items-center justify-center">
                    <span className="relative w-8 h-8 flex items-center justify-center border-2 border-foreground-950 bg-primary-500 text-foreground-950">
                      <span
                        className={`absolute inset-0 border-2 border-primary-600 pointer-events-none ${
                          flowActive ? "animate-flow-ring" : "opacity-0"
                        }`}
                        style={{ animationDelay: `${index * 1.2}s` }}
                        aria-hidden="true"
                      ></span>
                      <i
                        className={`ri-arrow-right-line text-lg ${flowActive ? "animate-flow-nudge" : ""}`}
                        style={{ animationDelay: `${index * 1.2}s` }}
                      ></i>
                    </span>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Speed & latency */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Speed"
            title="How fast an order actually travels"
            description="Execution is a relay race, and the baton changes hands in milliseconds. The part of the trip that does not get faster is where slippage is made."
          />
          <div className="mt-12">
            <Reveal>
              <LatencyTimeline />
            </Reveal>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {speedTradeoff.map((item, index) => (
              <Reveal key={item.title} delay={index * 90}>
                <div
                  className={`h-full flex flex-col border-2 border-foreground-950 p-6 md:p-8 ${
                    item.tone === "accent" ? "bg-accent-100" : "bg-primary-100"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 border-b-2 border-foreground-950 pb-4">
                    <MicroLabel
                      mark="dot"
                      tone={item.tone === "accent" ? "accent" : "primary"}
                    >
                      {item.anchor}
                    </MicroLabel>
                    <MicroLabel tone="muted">0{index + 1}</MicroLabel>
                  </div>
                  <h3 className="mt-5 font-heading font-black uppercase tracking-tight text-xl text-foreground-950">
                    {item.title}
                  </h3>
                  <ul className="mt-6 flex flex-col border-t-2 border-foreground-950">
                    {item.points.map((point) => (
                      <li
                        key={point}
                        className="flex items-start gap-3 py-4 border-b-2 border-foreground-950 text-sm leading-relaxed text-foreground-800"
                      >
                        <span
                          className="mt-[7px] block w-1.5 h-1.5 shrink-0 bg-foreground-950"
                          aria-hidden="true"
                        ></span>
                        {point}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={item.to}
                    className="mt-auto self-start pt-5 inline-flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.18em] font-semibold text-foreground-950 border-b-2 border-foreground-950 pb-1 hover:text-accent-700 hover:border-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {item.linkLabel}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Spread vs slippage */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Two costs"
            title="One you see coming, one you don't"
            description="Both come out of the same fill. The difference is whether the price is quoted before you click, or revealed only after it."
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Reveal>
              <div className="h-full flex flex-col border-2 border-foreground-950 bg-background-200 p-6 md:p-8">
                <div className="flex items-center justify-between gap-3 border-b-2 border-foreground-950 pb-4">
                  <MicroLabel mark="dot" tone="muted">
                    From your platform
                  </MicroLabel>
                  <MicroLabel tone="muted">01</MicroLabel>
                </div>
                <h3 className="mt-5 font-heading font-black uppercase tracking-tight text-xl md:text-2xl text-foreground-950">
                  Spread — the known cost
                </h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-800">
                  The spread is the gap between the bid and the ask, quoted on your platform so you
                  can see it before you trade. On EUR/USD it might be 1.0 pip; on an exotic pair, 20
                  pips or more.
                </p>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-800">
                  You pay it every time you cross the spread — which every market order does. Because
                  it is quoted up front, you can build it into your plan instead of being surprised
                  by it.
                </p>
                <div className="mt-auto pt-5">
                  <div className="flex items-end justify-between gap-4 border-t-2 border-foreground-950 pt-4">
                    <div>
                      <MicroLabel tone="muted">Typical · 1.0 pip · one standard lot</MicroLabel>
                      <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                        What 1.0 pip costs on one lot
                      </p>
                    </div>
                    <p className="font-heading font-black leading-none text-foreground-950 text-2xl md:text-3xl whitespace-nowrap">
                      $10
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="h-full flex flex-col border-2 border-foreground-950 bg-accent-100 p-6 md:p-8">
                <div className="flex items-center justify-between gap-3 border-b-2 border-foreground-950 pb-4">
                  <MicroLabel mark="dot" tone="accent">
                    From the fill itself
                  </MicroLabel>
                  <MicroLabel tone="muted">02</MicroLabel>
                </div>
                <h3 className="mt-5 font-heading font-black uppercase tracking-tight text-xl md:text-2xl text-foreground-950">
                  Slippage — the surprise cost
                </h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-800">
                  Slippage is the difference between the price you expected and the price you got. It
                  appears when quotes move faster than your order arrives, or when the book is thin.
                </p>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-800">
                  Limit orders eliminate it entirely: they can only fill at your price or better. The
                  trade-off is that they may not fill at all.
                </p>
                <div className="mt-auto pt-5">
                  <div className="flex items-end justify-between gap-4 border-t-2 border-foreground-950 pt-4">
                    <div>
                      <MicroLabel tone="muted">News release · 4.6 pips · one standard lot</MicroLabel>
                      <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                        What this news-time fill cost
                      </p>
                    </div>
                    <p className="font-heading font-black leading-none text-accent-700 text-2xl md:text-3xl whitespace-nowrap">
                      $46
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* worked slippage example */}
          <div className="mt-5">
            <Reveal>
              <div className="border-2 border-foreground-950 bg-background-50">
                <div className="px-5 py-4 bg-foreground-950 flex items-center justify-between border-b-2 border-foreground-950">
                  <MicroLabel tone="onDarkPrimary">Slippage in action — US Non-Farm Payrolls release</MicroLabel>
                  <MicroLabel tone="onDarkMuted">Worked example</MicroLabel>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 divide-y-2 md:divide-y-0 md:divide-x-2 divide-foreground-950">
                  {slippage.map((item) => (
                    <div key={item.label} className="p-6">
                      <MicroLabel tone="muted">{item.label}</MicroLabel>
                      <p
                        className={`mt-3 font-heading font-black text-3xl md:text-4xl leading-none ${
                          item.accent ? "text-accent-600" : "text-foreground-950"
                        }`}
                      >
                        {item.value}
                      </p>
                      <p className="mt-2 text-xs text-foreground-600">{item.note}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t-2 border-foreground-950 px-5 md:px-6 py-4 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
                  <MicroLabel mark="warn" tone="accent" className="shrink-0">
                    Do not add the spread again
                  </MicroLabel>
                  <p className="text-xs md:text-sm leading-relaxed text-foreground-600">
                    The 4.6 pips is the whole gap between the price you clicked and the price you
                    got — the spread widening at that moment is already inside it.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Total cost breakdown */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-t-2 border-foreground-950">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Total cost"
            title="Every cost that lands on a trade"
            description="Spread and slippage are only half the bill. A complete execution plan accounts for all four costs below — two you know in advance, one you only partly know, and one you can only manage."
          />

          <div className="mt-12 overflow-x-auto border-2 border-foreground-950">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <caption className="sr-only">
                The four costs that land on a forex trade — spread, commission, swap and slippage —
                and whether each is known before you trade.
              </caption>
              <thead>
                <tr className="bg-primary-500 text-foreground-950">
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">Cost</th>
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">What it is</th>
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">When it hits</th>
                  <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em]">Known in advance?</th>
                </tr>
              </thead>
              <tbody>
                {costRows.map((row) => (
                  <tr key={row.cost} className="border-t-2 border-foreground-950 bg-background-50">
                    <td className="px-5 py-5 align-top font-heading font-bold uppercase tracking-tight text-base text-foreground-950 border-r-2 border-foreground-950">
                      {row.cost}
                    </td>
                    <td className="px-5 py-5 align-top text-sm leading-relaxed text-foreground-600 border-r-2 border-foreground-950">
                      {row.what}
                    </td>
                    <td className="px-5 py-5 align-top text-sm leading-relaxed text-foreground-600 border-r-2 border-foreground-950">
                      {row.when}
                    </td>
                    <td className="px-5 py-5 align-top text-sm leading-relaxed text-foreground-800">
                      {row.known}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Reveal>
            <div className="mt-5 border-2 border-foreground-950 bg-background-50">
              <div className="px-5 py-4 bg-foreground-950 flex flex-wrap items-center justify-between gap-y-2 gap-x-3 border-b-2 border-foreground-950">
                <MicroLabel tone="onDarkPrimary">The whole bill — 1 lot EUR/USD, round trip, held one night</MicroLabel>
                <MicroLabel tone="onDarkMuted">Calculator defaults</MicroLabel>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-[2px] bg-foreground-950">
                {costExample.map((item) => (
                  <div
                    key={item.label}
                    className={`p-5 ${item.total ? "bg-accent-100" : "bg-background-50"}`}
                  >
                    <MicroLabel tone="muted">{item.label}</MicroLabel>
                    <p
                      className={`mt-3 font-heading font-black text-2xl md:text-3xl leading-none ${
                        item.total ? "text-accent-700" : "text-foreground-950"
                      }`}
                    >
                      {item.value}
                    </p>
                    <p className="mt-2 text-xs text-foreground-600">{item.note}</p>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-foreground-950 px-5 py-4 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
                <MicroLabel mark="arrow" className="shrink-0">
                  The part people forget
                </MicroLabel>
                <p className="text-xs md:text-sm leading-relaxed text-foreground-700">
                  This is one round trip. Twenty-five a month costs{" "}
                  <strong className="font-bold text-foreground-950">$630</strong> —{" "}
                  <strong className="font-bold text-foreground-950">6.3%</strong> of a $10k account,
                  before a single losing trade.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="mt-5">
            <Reveal>
              <CostCalculator />
            </Reveal>
          </div>
        </div>
      </section>

      <BrokerStrip
        label="Where execution cost is set"
        heading="Spreads are quoted by the broker, not by the market"
        note="The four costs above are the same everywhere in name — the numbers behind them are not."
        rationaleKey="order-execution"
      />

      <SessionLiquidity />

      {/* Risk tools */}
      <RiskTools />

      {/* Common misconceptions */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Common misconceptions"
            title="What traders blame — and what actually happens"
            description="Execution is where emotion meets mechanics, so myths flourish. Each one below costs more in discipline than any spread ever will in money."
          />

          <div className="mt-12 border-t-2 border-foreground-950">
            {myths.map((item, index) => (
              <Reveal key={item.myth}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-start py-7 border-b-2 border-foreground-950">
                  <div className="hidden lg:flex lg:col-span-1 items-start justify-end">
                    <span className="font-heading font-black text-3xl leading-none text-stroke text-foreground-950" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="lg:col-span-6">
                    <div className="flex items-start gap-3">
                      <span className="lg:hidden shrink-0 font-heading font-black text-2xl leading-none text-stroke text-foreground-950 mt-0.5" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <Tag tone="accent">The myth</Tag>
                        <h3 className="mt-4 font-heading font-bold uppercase tracking-tight text-base md:text-xl leading-snug text-foreground-950">
                          “{item.myth}”
                        </h3>
                        <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                          {item.why}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-5">
                    <Callout mark="check" label="What actually happens">
                      {item.reality}
                    </Callout>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Best practices */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <SectionHeading
              eyebrow="Checklist"
              title="Five habits that cut execution cost"
              description="None of these need a better broker or a bigger account. They are decisions made before the order leaves your platform."
            />
            <div className="mt-8 border-t-2 border-foreground-950 pt-5">
              <MicroLabel mark="warn" tone="muted">
                The trade-off
              </MicroLabel>
              <p className="mt-3 text-xs md:text-sm leading-relaxed text-foreground-600">
                Each of these lowers what a single fill costs. None of them rescues a weak setup,
                and waiting for the tighter session can mean missing the entry altogether. A saved
                spread on a trade you never took is no saving at all.
              </p>
              <p className="mt-3 text-xs md:text-sm leading-relaxed text-foreground-600">
                Three of the five are aimed squarely at slippage — the only one of the four costs
                you cannot price in advance.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8 border-t-2 border-foreground-950">
            <div className="flex items-center gap-4 py-3 border-b-2 border-foreground-950">
              <div className="min-w-0 flex-1 pl-[52px]">
                <MicroLabel tone="muted">The habit</MicroLabel>
              </div>
              <div className="hidden md:flex w-[176px] shrink-0 justify-end">
                <MicroLabel tone="muted">What it targets</MicroLabel>
              </div>
            </div>
            {bestPractices.map((item, index) => (
              <Reveal key={item.habit} delay={index * 60}>
                <div className="flex items-start gap-4 py-5 border-b-2 border-foreground-950">
                  <span className="w-9 h-9 shrink-0 flex items-center justify-center border-2 border-foreground-950 bg-primary-500 font-heading font-black text-sm text-foreground-950">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-sm md:text-base leading-relaxed font-medium text-foreground-950">
                      {item.habit}
                    </p>
                    <p className="mt-1.5 text-xs md:text-sm leading-relaxed text-foreground-600">
                      {item.why}
                    </p>
                    <div className="mt-2.5 flex md:hidden">
                      <MicroLabel mark="dot" tone="muted">
                        Targets {item.cost}
                      </MicroLabel>
                    </div>
                  </div>
                  <div className="hidden md:flex w-[176px] shrink-0 justify-end pt-1.5">
                    <MicroLabel mark="dot" tone="muted">
                      {item.cost}
                    </MicroLabel>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-4xl">
              Execution questions, answered
            </h2>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">
              The execution and slippage questions traders ask most — answered plainly.
            </p>
            <Link
              to="/glossary"
              className="mt-8 inline-flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground-950 border-b-2 border-foreground-950 pb-1 hover:text-accent-600 hover:border-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Browse the full glossary
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="lg:col-span-8">
            <Accordion items={faqs} />
          </div>
        </div>
      </section>

      <CtaBand
        title="Now put that fill next to the other three"
        description="You have seen what happens between clicking and being filled. Compare the four order types side by side and pick the one whose trade-off fits the way you actually trade."
        to="/#compare"
        buttonLabel="Open the comparison"
      />
    </>
  );
}